import React, { PropsWithChildren, ReactNode, useCallback, useMemo } from "react"
import { useRouter } from "next/router"
import Head from "next/head"
import { useECommerceMachine } from "../../app-core/_common/machines/machines-context"
import { FetchPageContent } from "../../app-core/_common/sanity/fetch-page-content"
import SectionTermsAndConditions from "../SectionTermsAndConditions/SectionTermsAndConditions"
import { canGoToNextStep } from "../../app-core/v3/_common/machines/ecommerce-machine/e-commerce-machine-transitions"
import { goto } from "../../app-core/_common/routing"
import { useBuildPageDataLayer } from "../../app-core/v3/_common/tracking/page-data-layer"
import { useGoToPayment } from "../../app-core/v3/guests-info/use-go-to-payment"
import { useSetActiveStep } from "../StepsNav/use-set-active-step"
import { VisibleMobile, VisibleTabletUp } from "../ResponsiveComponents"
import { PageViewAnalytics } from "../../app-core/v3/_common/hooks/use-page-view-analytics"
import Footer from "../Footer/Footer"
import StepsNav from "../StepsNav/StepsNav"
import { findActiveStep } from "../StepsNav/utils"
import Header from "../Header/Header"
import Sidebar from "../Sidebar/Sidebar"
import BottomBar from "../BottomBar/BottomBar"
import { BookingSteps } from "../../app-core/_common/steps/booking-steps"
import styles from "./SidebarLayout.module.scss"

type OnNext = () => void

interface Props {
    sanityContent: FetchPageContent
    onNext?: OnNext
    withStepsNav?: boolean
    withBottomBar?: boolean
    activeStep: BookingSteps
    sidebarBoxes?: ReactNode
}

const SidebarLayout: React.FC<PropsWithChildren<Props>> = ({
    children,
    activeStep,
    sanityContent,
    onNext,
    sidebarBoxes,
}) => {
    const [state] = useECommerceMachine()
    const step = state.context.flowVersion.tracking.step(activeStep)
    const pageDataLayer = useBuildPageDataLayer(step)
    const router = useRouter()
    const webQuoteExitIntent = state.context.flowVersion.ui.useWebQuoteExitIntent(state)

    const onNextDefault = useCallback(
        () => goto(router, state.context.flowVersion.router.routeNextStep(activeStep)),
        [activeStep, router, state],
    )
    const onNextInner = onNext ? onNext : onNextDefault
    const steps = useMemo(() => state.context.flowVersion.steps.steps(router, state), [router, state])
    const stepLabel = findActiveStep(steps, activeStep).label
    const titleLabel = `Silversea's Booking - ${stepLabel}`
    const showNextButton = state.context.flowVersion.ui.showNextButton(state)

    useSetActiveStep(activeStep, steps)
    useGoToPayment(state)

    return (
        <>
            <Head>
                <title>{titleLabel}</title>
            </Head>
            <div className={styles.page}>
                <Header className={styles.header} webQuoteExitIntent={webQuoteExitIntent} />
                <PageViewAnalytics data={pageDataLayer} />
                <main className={styles.body}>
                    <section className={styles.content}>
                        <StepsNav activeStep={activeStep} />
                        {children}
                    </section>
                    <Sidebar
                        className={styles.sidebar}
                        sanityContent={sanityContent}
                        activeStep={activeStep}
                        onNextClick={onNextInner}
                        isNextEnabled={canGoToNextStep(activeStep, state)}
                        showNextButton={showNextButton}
                        webQuoteExitIntent={webQuoteExitIntent}
                        boxes={sidebarBoxes}
                    />
                    <VisibleMobile>
                        <BottomBar
                            className={styles.sidebar}
                            sanityContent={sanityContent}
                            onNextClick={onNextInner}
                            isNextEnabled={canGoToNextStep(activeStep, state)}
                            showNextButton={showNextButton}
                            webQuoteExitIntent={webQuoteExitIntent}
                        />
                    </VisibleMobile>
                </main>

                <VisibleTabletUp>{state.context.flowVersion.ui.faq.FloatingButton(state, "page")}</VisibleTabletUp>
                {state.context.flowVersion.ui.faq.Section(state, sanityContent.dynamicFAQ)}

                <SectionTermsAndConditions
                    backgroundColor={state.context.flowVersion.ui.faq.isVisible(state) ? "neutral05" : "white"}
                    termsAndConditionsUrl={sanityContent.websiteUrls.termsAndConditions.path}
                />
                <Footer />
            </div>
        </>
    )
}

export default SidebarLayout
